import React, { useState, useEffect } from 'react';
import { ChevronDown, Send, CheckCircle, XCircle, Loader, AlertCircle, LogOut } from 'lucide-react';
import { resignationAPI, employeeAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import { getPrimaryRole } from '../utils/permissions';
import { getLocalToday } from '../utils/dateUtils';
import DateInput from './DateInput';
import EstablishmentCard from './EstablishmentCard';
import './ApplyLeave.css';

const DEFAULT_NOTICE_DAYS = 30;

const addDays = (dateStr, days) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + parseInt(days || 0, 10));
  return d.toISOString().slice(0, 10);
};

const getInitials = (first = '', last = '') =>
  `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'E';

const EMPTY = () => ({
  resignation_date: getLocalToday(),
  notice_period_days: String(DEFAULT_NOTICE_DAYS),
  last_working_day: addDays(getLocalToday(), DEFAULT_NOTICE_DAYS),
  reason: '',
});

// ── Shared form ─────────────────────────────────────────────────────
function ResignationForm({ profile, history, onSubmitted }) {
  const [form, setForm] = useState(EMPTY());
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const set = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'resignation_date' || field === 'notice_period_days') {
        next.last_working_day = addDays(
          field === 'resignation_date' ? value : prev.resignation_date,
          field === 'notice_period_days' ? value : prev.notice_period_days,
        );
      } else if (field === 'last_working_day' && next.resignation_date) {
        const diff = Math.round((new Date(value) - new Date(next.resignation_date)) / 86400000);
        next.notice_period_days = String(Math.max(0, diff));
      }
      return next;
    });
  };

  const hasActive = (history || []).some(h => h.status === 'pending' || h.status === 'approved');

  const submit = async () => {
    if (!profile?.employee_id) {
      setToast({ type: 'error', msg: 'No employee selected.' });
      return;
    }
    if (!form.reason?.trim()) {
      setToast({ type: 'error', msg: 'Please give a reason for the resignation.' });
      return;
    }
    if (!form.resignation_date || !form.last_working_day) {
      setToast({ type: 'error', msg: 'Both resignation date and last working day are required.' });
      return;
    }
    if (new Date(form.last_working_day) < new Date(form.resignation_date)) {
      setToast({ type: 'error', msg: 'Last working day cannot be before resignation date.' });
      return;
    }
    if (!window.confirm(
      `Submit resignation for ${profile.first_name} ${profile.last_name}?\nLast working day: ${form.last_working_day}\nThis will be sent to the manager for approval.`,
    )) return;

    setSubmitting(true);
    try {
      await resignationAPI.apply({
        employeeId: profile.employee_id,
        employeeName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
        establishmentId: profile.establishment_id || null,
        departmentId: profile.department || null,
        managerId: profile.manager_id || null,
        approverId: profile.manager_id || null,
        approverName: profile.manager_name || 'Manager',
        resignationDate: form.resignation_date,
        lastWorkingDay: form.last_working_day,
        noticePeriodDays: parseInt(form.notice_period_days || 0, 10),
        reason: form.reason,
      });
      notifySavedAndReload('Resignation submitted — awaiting manager approval.');
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to submit resignation.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {toast && (
        <div className={`al-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {hasActive && (
        <div className="al-card" style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb' }}>
          <p className="al-card-label" style={{ color: '#92400e' }}>
            <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
            This employee already has a resignation on file ({history[0]?.status}). Submitting another may be rejected.
          </p>
        </div>
      )}

      <div className="al-card">
        <p className="al-card-label"><LogOut size={14} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} /> Resignation Details</p>
        <div className="al-form-grid">
          <div className="al-field">
            <label className="al-label">Resignation Date <span>*</span></label>
            <DateInput value={form.resignation_date} onChange={e => set('resignation_date', e.target.value)} className="al-input" />
          </div>
          <div className="al-field">
            <label className="al-label">Notice Period (days)</label>
            <input type="number" min="0" value={form.notice_period_days}
              onChange={e => set('notice_period_days', e.target.value)} className="al-input" />
          </div>
          <div className="al-field">
            <label className="al-label">Last Working Day <span>*</span></label>
            <DateInput value={form.last_working_day} onChange={e => set('last_working_day', e.target.value)}
              className="al-input" min={form.resignation_date} />
          </div>
          <div className="al-field" style={{ gridColumn: '1 / -1' }}>
            <label className="al-label">Reason <span>*</span></label>
            <textarea value={form.reason} onChange={e => set('reason', e.target.value)}
              className="al-input" rows={4}
              placeholder="Brief reason for leaving (e.g., new opportunity, personal reasons, relocation)…" />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button onClick={submit} disabled={submitting}
            className="mp-btn-save" style={{ background: '#dc2626' }}>
            {submitting ? <Loader size={15} className="mp-spinner-sm" /> : <Send size={15} />}
            {submitting ? 'Submitting…' : 'Submit Resignation'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── History panel ───────────────────────────────────────────────────
function HistoryPanel({ history }) {
  if (!history?.length) return null;
  return (
    <div className="al-card">
      <p className="al-card-label">Resignation History</p>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {history.map(h => (
          <div key={h.request_id} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0.75rem 1rem', border: '1px solid #e5e7eb', borderRadius: '8px',
            background: 'var(--theme-surface, #fff)',
          }}>
            <div>
              <div style={{ fontWeight: 600 }}>Last day: {h.last_working_day}</div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                Submitted {h.applied_date?.slice(0, 10)} · {h.reason?.slice(0, 80) || '—'}
              </div>
            </div>
            <span style={{
              padding: '0.25rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
              background: h.status === 'approved' ? '#d1fae5'
                : h.status === 'rejected' ? '#fee2e2'
                : h.status === 'withdrawn' ? '#f3f4f6' : '#fef3c7',
              color: h.status === 'approved' ? '#065f46'
                : h.status === 'rejected' ? '#991b1b'
                : h.status === 'withdrawn' ? '#374151' : '#92400e',
            }}>{h.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Employee self-service ──────────────────────────────────────────
function EmployeeApplyResignation({ user }) {
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reloadHistory = async (empId) => {
    try {
      const res = await resignationAPI.getByEmployee(empId);
      setHistory(res.resignations || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await employeeAPI.getMe();
        setProfile(me);
        if (me?.employee_id) await reloadHistory(me.employee_id);
      } catch (err) {
        setError(err.message || 'Could not load profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="al-page"><div className="al-state-center"><Loader size={28} className="al-spinner-lg" /><p>Loading…</p></div></div>
  );
  if (!profile) return (
    <div className="al-page"><div className="al-state-center al-state-warn">
      <AlertCircle size={28} />
      <p>No employee record is linked to your account ({user?.email}).<br />Please contact your administrator.</p>
    </div></div>
  );

  return (
    <div className="al-page">
      <div className="al-hero">
        <div className="al-hero-avatar">{getInitials(profile.first_name, profile.last_name)}</div>
        <div className="al-hero-info">
          <h1 className="al-hero-name">{profile.first_name} {profile.last_name}</h1>
          <div className="al-hero-meta">
            {profile.position && <span>{profile.position}</span>}
            {profile.department && <><span className="al-hero-dot">·</span><span>{profile.department}</span></>}
          </div>
          <div className="al-hero-id">Employee ID: {profile.employee_id}</div>
        </div>
      </div>
      <ResignationForm profile={profile} history={history}
        onSubmitted={() => reloadHistory(profile.employee_id)} />
      <HistoryPanel history={history} />
    </div>
  );
}

// ── Admin / Manager view ────────────────────────────────────────────
function AdminApplyResignation() {
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEst, setSelectedEst] = useState(lockedEstId || '');
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [estLoading, setEstLoading] = useState(true);
  const [empLoading, setEmpLoading] = useState(false);

  useEffect(() => {
    if (lockedEstId) setSelectedEst(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) { setEstLoading(false); return; }
    establishmentAPI.getAll()
      .then(r => setEstablishments(r.establishments || []))
      .finally(() => setEstLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEst) { setEmployees([]); setSelectedEmpId(''); setSelectedProfile(null); return; }
    setEmpLoading(true);
    employeeAPI.getByEstablishment(selectedEst)
      .then(r => setEmployees(r.employees || []))
      .catch(() => setEmployees([]))
      .finally(() => setEmpLoading(false));
    setSelectedEmpId('');
    setSelectedProfile(null);
    setHistory([]);
  }, [selectedEst]);

  const reloadHistory = async (empId) => {
    try {
      const res = await resignationAPI.getByEmployee(empId);
      setHistory(res.resignations || []);
    } catch { setHistory([]); }
  };

  const handleEmployeeChange = (empId) => {
    setSelectedEmpId(empId);
    if (!empId) { setSelectedProfile(null); setHistory([]); return; }
    const emp = employees.find(e => String(e.employee_id) === String(empId));
    if (emp) {
      setSelectedProfile(emp);
      reloadHistory(emp.employee_id);
    }
  };

  return (
    <div className="al-page">
      <div className="al-toolbar">
        <h1 className="al-page-title">Apply Resignation</h1>
      </div>

      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEst}
          onChange={e => setSelectedEst(e.target.value)}
          loading={estLoading}
        />
      )}

      {selectedEst && (
        <div className="est-card-wrap">
          <label className="est-label">Employee</label>
          <div className="est-dropdown">
            <select
              className="est-select"
              value={selectedEmpId}
              onChange={e => handleEmployeeChange(e.target.value)}
              disabled={empLoading}
            >
              <option value="">{empLoading ? 'Loading…' : 'Select employee'}</option>
              {employees.map(e => (
                <option key={e.employee_id} value={e.employee_id}>
                  {e.first_name} {e.last_name} ({e.employee_id})
                </option>
              ))}
            </select>
            <ChevronDown className="est-chevron" size={16} />
          </div>
        </div>
      )}

      {selectedProfile && (
        <>
          <div className="al-hero" style={{ marginBottom: '1rem' }}>
            <div className="al-hero-avatar">{getInitials(selectedProfile.first_name, selectedProfile.last_name)}</div>
            <div className="al-hero-info">
              <h1 className="al-hero-name">{selectedProfile.first_name} {selectedProfile.last_name}</h1>
              <div className="al-hero-meta">
                {selectedProfile.position && <span>{selectedProfile.position}</span>}
                {selectedProfile.department && <><span className="al-hero-dot">·</span><span>{selectedProfile.department}</span></>}
              </div>
              <div className="al-hero-id">Employee ID: {selectedProfile.employee_id}</div>
            </div>
          </div>
          <ResignationForm profile={selectedProfile} history={history}
            onSubmitted={() => reloadHistory(selectedProfile.employee_id)} />
          <HistoryPanel history={history} />
        </>
      )}
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────
export default function ApplyResignation() {
  const { user } = useAuth();
  const role = getPrimaryRole(user);
  if (role === 'SuperAdmin' || role === 'ClientAdmin') return <AdminApplyResignation />;
  return <EmployeeApplyResignation user={user} />;
}
